/**
 * Bundle builder: fill 4 slots, then add the BYOB parent and open the cart drawer.
 */
import { CartAddEvent } from '@theme/events';
class BundleBuilder extends HTMLElement {
  #selections = [];
  #activeFilter = 'all';
  #searchQuery = '';
  #pageSize = 20;
  #visibleCount = 20;
  #sectionObserver = null;
  #stickyMq = null;
  #sectionInView = false;
  #stackBottomReached = false;
  #stackOutOfView = false;
  #onStickyMqChange = null;
  #onStickyScroll = null;
  #priceObserver = null;
  #priceFitFrame = 0;
  #checkoutToken = 0;
  #onPageShow = null;
  #storageKey = 'rvnche-bundle-selections';

  connectedCallback() {
    this.requiredCount = Number(this.dataset.requiredCount || 4);
    this.parentVariantId = Number(this.dataset.parentVariantId || 0);
    this.#pageSize = Number(this.dataset.pageSize || 20);
    this.#visibleCount = this.#pageSize;
    this.#storageKey = `rvnche-bundle-selections:${window.Shopify?.shop || location.host}`;
    this.#selections = Array.from({ length: this.requiredCount }, () => null);

    this.slots = [...this.querySelectorAll('[data-slot]')];
    this.dockSlots = [...this.querySelectorAll('[data-dock-slot]')];
    this.dock = this.querySelector('[data-dock]');
    this.stickyBar = this.querySelector('[data-sticky]');
    this.slotsSentinel = this.querySelector('[data-slots]');
    this.sectionRoot = this.closest('.bundle-builder') || this;
    this.cards = [...this.querySelectorAll('[data-product-card]')];
    this.statusEl = this.querySelector('[data-status]');
    this.selectedCountEls = [...this.querySelectorAll('[data-selected-count]')];
    this.remainingEl = this.querySelector('[data-remaining]');
    this.checkoutBtn = this.querySelector('[data-checkout]');
    this.searchInput = this.querySelector('[data-search]');
    this.loadMoreWrap = this.querySelector('[data-load-more-wrap]');
    this.loadMoreBtn = this.querySelector('[data-load-more]');

    this.cards.forEach((card) => this.#initCard(card));
    this.querySelectorAll('[data-filter]').forEach((btn) => {
      btn.addEventListener('click', () => this.#setFilter(btn));
    });
    this.slots.forEach((slot) => {
      slot.addEventListener('click', (event) => this.#onSlotClick(event, Number(slot.dataset.slot)));
    });
    this.dockSlots.forEach((slot) => {
      slot.addEventListener('click', (event) => this.#onSlotClick(event, Number(slot.dataset.dockSlot)));
    });
    this.searchInput?.addEventListener('input', () => {
      this.#searchQuery = this.searchInput.value.trim().toLowerCase();
      this.#visibleCount = this.#pageSize;
      this.#filterCards();
    });
    this.loadMoreBtn?.addEventListener('click', () => this.#loadMore());
    this.checkoutBtn?.addEventListener('click', () => this.#checkout());
    this.#onPageShow = (event) => {
      if (!event.persisted) return;
      this.#checkoutToken += 1;
      this.#setStatus('');
      this.#render();
    };
    window.addEventListener('pageshow', this.#onPageShow);
    this.#observeSticky();
    this.#restoreSelections();
    this.#pruneUnavailableSelections(true);
    this.#filterCards();
    this.#render();
    this.#queueFitStickyPrice();
  }

  disconnectedCallback() {
    if (this.#onPageShow) window.removeEventListener('pageshow', this.#onPageShow);
    this.#sectionObserver?.disconnect();
    this.#priceObserver?.disconnect();
    if (this.#priceFitFrame) cancelAnimationFrame(this.#priceFitFrame);
    this.#stickyMq?.removeEventListener?.('change', this.#onStickyMqChange);
    if (this.#onStickyScroll) {
      window.removeEventListener('scroll', this.#onStickyScroll);
      window.removeEventListener('resize', this.#onStickyScroll);
    }
  }

  #normalizeSelection(item) {
    if (!item || typeof item !== 'object') return null;
    const variantId = Number(item.variantId);
    if (!variantId) return null;
    return {
      productId: String(item.productId || ''),
      productTitle: String(item.productTitle || ''),
      variantId,
      variantTitle: String(item.variantTitle || ''),
      image: String(item.image || ''),
      sbHash: String(item.sbHash || this.#variantHash(variantId)),
    };
  }

  #variantHash(variantId) {
    return this.#md5(String(variantId)).slice(0, 8);
  }

  #md5(string) {
    // Matches Simple Bundles: first 8 chars of md5(String(variantId))
    const add32 = (a, b) => (a + b) & 0xffffffff;
    const cmn = (q, a, b, x, s, t) => {
      a = add32(add32(a, q), add32(x, t));
      return add32((a << s) | (a >>> (32 - s)), b);
    };
    const ff = (a, b, c, d, x, s, t) => cmn((b & c) | (~b & d), a, b, x, s, t);
    const gg = (a, b, c, d, x, s, t) => cmn((b & d) | (c & ~d), a, b, x, s, t);
    const hh = (a, b, c, d, x, s, t) => cmn(b ^ c ^ d, a, b, x, s, t);
    const ii = (a, b, c, d, x, s, t) => cmn(c ^ (b | ~d), a, b, x, s, t);

    const md5blk = (s) => {
      const blks = [];
      for (let i = 0; i < 64; i += 4) {
        blks[i >> 2] =
          s.charCodeAt(i) +
          (s.charCodeAt(i + 1) << 8) +
          (s.charCodeAt(i + 2) << 16) +
          (s.charCodeAt(i + 3) << 24);
      }
      return blks;
    };

    const md5cycle = (x, k) => {
      let [a, b, c, d] = x;
      a = ff(a, b, c, d, k[0], 7, -680876936);
      d = ff(d, a, b, c, k[1], 12, -389564586);
      c = ff(c, d, a, b, k[2], 17, 606105819);
      b = ff(b, c, d, a, k[3], 22, -1044525330);
      a = ff(a, b, c, d, k[4], 7, -176418897);
      d = ff(d, a, b, c, k[5], 12, 1200080426);
      c = ff(c, d, a, b, k[6], 17, -1473231341);
      b = ff(b, c, d, a, k[7], 22, -45705983);
      a = ff(a, b, c, d, k[8], 7, 1770035416);
      d = ff(d, a, b, c, k[9], 12, -1958414417);
      c = ff(c, d, a, b, k[10], 17, -42063);
      b = ff(b, c, d, a, k[11], 22, -1990404162);
      a = ff(a, b, c, d, k[12], 7, 1804603682);
      d = ff(d, a, b, c, k[13], 12, -40341101);
      c = ff(c, d, a, b, k[14], 17, -1502002290);
      b = ff(b, c, d, a, k[15], 22, 1236535329);
      a = gg(a, b, c, d, k[1], 5, -165796510);
      d = gg(d, a, b, c, k[6], 9, -1069501632);
      c = gg(c, d, a, b, k[11], 14, 643717713);
      b = gg(b, c, d, a, k[0], 20, -373897302);
      a = gg(a, b, c, d, k[5], 5, -701558691);
      d = gg(d, a, b, c, k[10], 9, 38016083);
      c = gg(c, d, a, b, k[15], 14, -660478335);
      b = gg(b, c, d, a, k[4], 20, -405537848);
      a = gg(a, b, c, d, k[9], 5, 568446438);
      d = gg(d, a, b, c, k[14], 9, -1019803690);
      c = gg(c, d, a, b, k[3], 14, -187363961);
      b = gg(b, c, d, a, k[8], 20, 1163531501);
      a = gg(a, b, c, d, k[13], 5, -1444681467);
      d = gg(d, a, b, c, k[2], 9, -51403784);
      c = gg(c, d, a, b, k[7], 14, 1735328473);
      b = gg(b, c, d, a, k[12], 20, -1926607734);
      a = hh(a, b, c, d, k[5], 4, -378558);
      d = hh(d, a, b, c, k[8], 11, -2022574463);
      c = hh(c, d, a, b, k[11], 16, 1839030562);
      b = hh(b, c, d, a, k[14], 23, -35309556);
      a = hh(a, b, c, d, k[1], 4, -1530992060);
      d = hh(d, a, b, c, k[4], 11, 1272893353);
      c = hh(c, d, a, b, k[7], 16, -155497632);
      b = hh(b, c, d, a, k[10], 23, -1094730640);
      a = hh(a, b, c, d, k[13], 4, 681279174);
      d = hh(d, a, b, c, k[0], 11, -358537222);
      c = hh(c, d, a, b, k[3], 16, -722521979);
      b = hh(b, c, d, a, k[6], 23, 76029189);
      a = hh(a, b, c, d, k[9], 4, -640364487);
      d = hh(d, a, b, c, k[12], 11, -421815835);
      c = hh(c, d, a, b, k[15], 16, 530742520);
      b = hh(b, c, d, a, k[2], 23, -995338651);
      a = ii(a, b, c, d, k[0], 6, -198630844);
      d = ii(d, a, b, c, k[7], 10, 1126891415);
      c = ii(c, d, a, b, k[14], 15, -1416354905);
      b = ii(b, c, d, a, k[5], 21, -57434055);
      a = ii(a, b, c, d, k[12], 6, 1700485571);
      d = ii(d, a, b, c, k[3], 10, -1894986606);
      c = ii(c, d, a, b, k[10], 15, -1051523);
      b = ii(b, c, d, a, k[1], 21, -2054922799);
      a = ii(a, b, c, d, k[8], 6, 1873313359);
      d = ii(d, a, b, c, k[15], 10, -30611744);
      c = ii(c, d, a, b, k[6], 15, -1560198380);
      b = ii(b, c, d, a, k[13], 21, 1309151649);
      a = ii(a, b, c, d, k[4], 6, -145523070);
      d = ii(d, a, b, c, k[11], 10, -1120210379);
      c = ii(c, d, a, b, k[2], 15, 718787259);
      b = ii(b, c, d, a, k[9], 21, -343485551);
      x[0] = add32(a, x[0]);
      x[1] = add32(b, x[1]);
      x[2] = add32(c, x[2]);
      x[3] = add32(d, x[3]);
    };

    const md51 = (s) => {
      const n = s.length;
      const state = [1732584193, -271733879, -1732584194, 271733878];
      let i;
      for (i = 64; i <= n; i += 64) {
        md5cycle(state, md5blk(s.substring(i - 64, i)));
      }
      s = s.substring(i - 64);
      const tail = new Array(16).fill(0);
      for (i = 0; i < s.length; i++) {
        tail[i >> 2] |= s.charCodeAt(i) << ((i % 4) << 3);
      }
      tail[i >> 2] |= 0x80 << ((i % 4) << 3);
      if (i > 55) {
        md5cycle(state, tail);
        for (let j = 0; j < 16; j++) tail[j] = 0;
      }
      tail[14] = n * 8;
      md5cycle(state, tail);
      return state;
    };

    const hexChr = '0123456789abcdef'.split('');
    const rhex = (n) => {
      let output = '';
      for (let j = 0; j < 4; j++) {
        output += hexChr[(n >> (j * 8 + 4)) & 0x0f] + hexChr[(n >> (j * 8)) & 0x0f];
      }
      return output;
    };

    return md51(unescape(encodeURIComponent(string)))
      .map(rhex)
      .join('');
  }

  #restoreSelections() {
    try {
      // Drop any previous localStorage copy so selections don't survive sessions.
      localStorage.removeItem(this.#storageKey);

      const raw = sessionStorage.getItem(this.#storageKey);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;

      this.#selections = Array.from({ length: this.requiredCount }, (_, index) =>
        this.#normalizeSelection(parsed[index])
      );

      const restored = this.#selectedCount();
      if (restored > 0) {
        this.#setStatus(`Restored ${restored} piece${restored === 1 ? '' : 's'} from this session.`);
      }
    } catch {
      // Ignore invalid storage
    }
  }

  #persistSelections() {
    try {
      const hasItems = this.#selections.some(Boolean);
      if (!hasItems) {
        sessionStorage.removeItem(this.#storageKey);
        return;
      }
      sessionStorage.setItem(this.#storageKey, JSON.stringify(this.#selections));
    } catch {
      // Private mode / quota errors
    }
  }

  #clearPersistedSelections() {
    try {
      sessionStorage.removeItem(this.#storageKey);
      localStorage.removeItem(this.#storageKey);
    } catch {
      // Ignore
    }
  }

  #observeSticky() {
    if (!this.stickyBar || !this.sectionRoot) return;

    this.#stickyMq = window.matchMedia('(max-width: 749px)');
    this.#onStickyMqChange = () => this.#syncStickyVisibility();
    this.#stickyMq.addEventListener?.('change', this.#onStickyMqChange);

    this.#sectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.#sectionInView = entry.isIntersecting;
        this.#syncStickyVisibility();
      },
      { threshold: 0, rootMargin: '0px' }
    );
    this.#sectionObserver.observe(this.sectionRoot);

    this.#onStickyScroll = () => {
      this.#updateStackPositions();
      this.#syncStickyVisibility();
    };
    window.addEventListener('scroll', this.#onStickyScroll, { passive: true });
    window.addEventListener('resize', this.#onStickyScroll, { passive: true });
    if (typeof ResizeObserver !== 'undefined' && this.stickyBar) {
      this.#priceObserver = new ResizeObserver(() => this.#queueFitStickyPrice());
      this.#priceObserver.observe(this.stickyBar);
    }
    this.#updateStackPositions();
    this.#syncStickyVisibility();
  }

  #queueFitStickyPrice() {
    if (this.#priceFitFrame) cancelAnimationFrame(this.#priceFitFrame);
    this.#priceFitFrame = requestAnimationFrame(() => {
      this.#priceFitFrame = 0;
      this.#fitStickyPrice();
    });
  }

  #measureWidth(node, styles, childStyles = {}) {
    const probe = node.cloneNode(true);
    const parent = node.parentElement;
    if (!parent) return 0;

    probe.style.position = 'absolute';
    probe.style.left = '0';
    probe.style.top = '0';
    probe.style.visibility = 'hidden';
    probe.style.pointerEvents = 'none';
    probe.style.width = 'max-content';
    probe.style.maxWidth = 'none';
    probe.style.whiteSpace = 'nowrap';
    Object.entries(styles).forEach(([key, value]) => {
      probe.style[key] = value;
    });
    Object.entries(childStyles).forEach(([selector, style]) => {
      probe.querySelectorAll(selector).forEach((el) => {
        Object.entries(style).forEach(([key, value]) => {
          el.style[key] = value;
        });
      });
    });
    parent.appendChild(probe);
    const width = probe.getBoundingClientRect().width;
    probe.remove();
    return width;
  }

  #fitStickyPrice() {
    const title = this.querySelector('.bundle-builder__sticky-title');
    const bar = this.stickyBar;
    const button = this.checkoutBtn;
    const row = bar?.querySelector('.bundle-builder__sticky-inner');
    if (!title || !bar || !button || !row) return;
    if (!this.#isMobile() || !title.getClientRects().length) {
      ['--sticky-title-size', '--sticky-price-size', '--sticky-button-size', '--sticky-button-min', '--sticky-button-pad', '--sticky-button-track'].forEach((key) => {
        bar.style.removeProperty(key);
      });
      return;
    }

    const rowStyle = getComputedStyle(row);
    const gap = parseFloat(rowStyle.columnGap) || parseFloat(rowStyle.gap) || 0;
    const pad = (parseFloat(rowStyle.paddingLeft) || 0) + (parseFloat(rowStyle.paddingRight) || 0);
    const available = row.clientWidth - pad;
    if (available <= 0) return;

    const steps = [
      { title: 'clamp(16px, 4.6vw, 21px)', price: '1.14em', button: '12px', min: '52px', pad: '10px 8px', track: '0.6px' },
      { title: '15px', price: '1.05em', button: '11px', min: '46px', pad: '8px 6px', track: '0.35px' },
      { title: '13px', price: '1em', button: '10px', min: '40px', pad: '7px 4px', track: '0.2px' },
      { title: '12px', price: '1em', button: '9px', min: '36px', pad: '6px 3px', track: '0.1px' },
    ];

    let fit = steps[steps.length - 1];
    for (const step of steps) {
      const titleWidth = this.#measureWidth(title, { fontSize: step.title }, { strong: { fontSize: step.price } });
      const buttonWidth = this.#measureWidth(button, {
        fontSize: step.button,
        minHeight: step.min,
        padding: step.pad,
        letterSpacing: step.track,
      });
      if (titleWidth + gap + buttonWidth <= available + 1) {
        fit = step;
        break;
      }
    }

    const useDefault = fit === steps[0];
    const vars = {
      '--sticky-title-size': useDefault ? '' : fit.title,
      '--sticky-price-size': useDefault ? '' : fit.price,
      '--sticky-button-size': useDefault ? '' : fit.button,
      '--sticky-button-min': useDefault ? '' : fit.min,
      '--sticky-button-pad': useDefault ? '' : fit.pad,
      '--sticky-button-track': useDefault ? '' : fit.track,
    };
    Object.entries(vars).forEach(([key, value]) => {
      if (value) bar.style.setProperty(key, value);
      else bar.style.removeProperty(key);
    });
  }

  #isMobile() {
    return this.#stickyMq?.matches ?? window.matchMedia('(max-width: 749px)').matches;
  }

  #updateStackPositions() {
    if (!this.slotsSentinel) {
      this.#stackBottomReached = true;
      this.#stackOutOfView = true;
      return;
    }
    const bottom = this.slotsSentinel.getBoundingClientRect().bottom;
    // Mobile: sticky after main-stack bottom has entered/passed the viewport
    this.#stackBottomReached = bottom <= window.innerHeight;
    // Desktop: dock after main stack has left above the viewport
    this.#stackOutOfView = bottom < 0;
  }

  #syncStickyVisibility() {
    if (!this.stickyBar) return;

    const mobile = this.#isMobile();
    this.#updateStackPositions();

    let show = false;
    let showDock = false;

    if (mobile) {
      // Mobile: sticky only after stack bottom is in view; blocks always with sticky
      show = this.#sectionInView && this.#stackBottomReached;
      showDock = show;
    } else {
      // Desktop: sticky while section in view; blocks after stack scrolled away
      show = this.#sectionInView;
      showDock = show && this.#stackOutOfView;
    }

    this.stickyBar.classList.toggle('is-visible', show);
    this.stickyBar.classList.toggle('has-dock', showDock);
    this.stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
    this.dock?.setAttribute('aria-hidden', showDock ? 'false' : 'true');
  }

  #initCard(card) {
    try {
      card._variants = JSON.parse(card.querySelector('[data-variants]')?.textContent || '[]');
    } catch {
      card._variants = [];
    }

    const preferred = card._variants.find((variant) => variant.available) || card._variants[0];
    card._selectedOptions = [...(preferred?.options || [])];

    card.querySelectorAll('[data-option-select]').forEach((select) => {
      const optionEl = select.closest('[data-option-index]');
      const index = Number(optionEl?.dataset.optionIndex || 0);
      if (card._selectedOptions[index] != null) {
        select.value = String(card._selectedOptions[index]);
      }
      select.addEventListener('change', () => {
        card._selectedOptions[index] = select.value;
        this.#syncCardAvailability(card);
      });
    });

    card.querySelector('[data-add]')?.addEventListener('click', () => this.#addSelection(card));
    this.#initGallery(card);
    this.#syncCardAvailability(card);
  }

  #initGallery(card) {
    const track = card.querySelector('[data-gallery-track]');
    if (!track) return;

    const dots = [...card.querySelectorAll('[data-gallery-dot]')];
    if (dots.length < 2) return;

    let frame = 0;
    const update = () => this.#updateGalleryDots(track);
    track.addEventListener(
      'scroll',
      () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          update();
        });
      },
      { passive: true }
    );

    dots.forEach((dot) => {
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        const index = Number(dot.dataset.index || 0);
        this.#scrollGallery(track, index);
      });
    });

    track.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const width = track.clientWidth || 1;
      const current = Math.round(track.scrollLeft / width);
      const next = event.key === 'ArrowRight' ? current + 1 : current - 1;
      this.#scrollGallery(track, next);
    });

    let drag = null;
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      drag = { x: event.clientX, left: track.scrollLeft, moved: false };
    });
    track.addEventListener('pointermove', (event) => {
      if (!drag) return;
      const dx = event.clientX - drag.x;
      if (Math.abs(dx) <= 5) return;
      drag.moved = true;
      track.classList.add('is-dragging');
      track.setPointerCapture?.(event.pointerId);
      track.scrollLeft = drag.left - dx;
      event.preventDefault();
    });
    const endDrag = () => {
      if (!drag?.moved) {
        drag = null;
        return;
      }
      drag = null;
      track.classList.remove('is-dragging');
      const width = track.clientWidth || 1;
      const index = Math.round(track.scrollLeft / width);
      this.#scrollGallery(track, index);
    };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        if (track.dataset.pendingIndex == null || track.dataset.pendingIndex === '') return;
        if (!track.clientWidth) return;
        const index = Number(track.dataset.pendingIndex);
        delete track.dataset.pendingIndex;
        track.scrollTo({ left: index * track.clientWidth, behavior: 'auto' });
        this.#updateGalleryDots(track);
      });
      observer.observe(track);
    }
  }

  #updateGalleryDots(track) {
    const width = track.clientWidth;
    if (!width) return;
    const dots = track.parentElement?.querySelectorAll('[data-gallery-dot]');
    if (!dots?.length) return;
    const index = Math.max(0, Math.min(dots.length - 1, Math.round(track.scrollLeft / width)));
    dots.forEach((dot) => {
      const active = Number(dot.dataset.index) === index;
      dot.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  #scrollGallery(track, index) {
    const dots = track.parentElement?.querySelectorAll('[data-gallery-dot]');
    const max = Math.max(0, (dots?.length || track.children.length) - 1);
    const next = Math.max(0, Math.min(max, index));
    const width = track.clientWidth;
    if (!width) {
      track.dataset.pendingIndex = String(next);
      return;
    }
    delete track.dataset.pendingIndex;
    track.scrollTo({ left: next * width, behavior: 'auto' });
    this.#updateGalleryDots(track);
  }

  #syncGalleryToVariant(card, variant) {
    const track = card.querySelector('[data-gallery-track]');
    if (!track || variant?.imageId == null) return;
    if (String(card._galleryImageId ?? '') === String(variant.imageId)) return;

    const slides = [...track.children];
    const index = slides.findIndex((slide) => slide.dataset.imageId === String(variant.imageId));
    if (index < 0) return;

    card._galleryImageId = variant.imageId;
    this.#scrollGallery(track, index);
  }

  #findVariantById(variantId) {
    const id = Number(variantId);
    for (const card of this.cards) {
      const match = (card._variants || []).find((variant) => Number(variant.id) === id);
      if (match) return match;
    }
    return null;
  }

  #isSelectionAvailable(selection) {
    if (!selection?.variantId) return false;
    const variant = this.#findVariantById(selection.variantId);
    return Boolean(variant?.available);
  }

  #pruneUnavailableSelections(announce = false) {
    let removed = 0;
    this.#selections = this.#selections.map((item) => {
      if (!item) return null;
      if (this.#isSelectionAvailable(item)) return item;
      removed += 1;
      return null;
    });

    if (announce && removed > 0) {
      this.#setStatus(
        `${removed} out-of-stock piece${removed === 1 ? '' : 's'} removed. Select ${this.requiredCount} in-stock pieces.`
      );
    }

    return removed;
  }

  #syncCardAvailability(card) {
    const variant = this.#findVariant(card);
    const addBtn = card.querySelector('[data-add]');
    if (!addBtn) return;
    const available = Boolean(variant?.available);
    addBtn.disabled = !available;
    this.#syncGalleryToVariant(card, variant);
    this.#syncCardPrice(card, variant);
    this.#cacheAddLabels(addBtn);
    if (addBtn.classList.contains('is-added')) return;
    this.#setAddLabel(addBtn, available ? 'default' : 'Sold out');
  }

  #syncCardPrice(card, variant) {
    const current = card.querySelector('[data-price-current]');
    const compare = card.querySelector('[data-price-compare]');
    if (current && variant?.priceLabel) current.textContent = variant.priceLabel;
    if (!compare) return;
    if (variant?.compareLabel) {
      compare.hidden = false;
      compare.textContent = variant.compareLabel;
    } else {
      compare.hidden = true;
      compare.textContent = '';
    }
  }

  #cacheAddLabels(addBtn) {
    if (!addBtn.dataset.label) {
      addBtn.dataset.label = addBtn.getAttribute('aria-label') || 'Add to stack';
    }
  }

  #setAddLabel(addBtn, nextText) {
    const label = nextText === 'default' ? addBtn.dataset.label || 'Add to stack' : nextText;
    addBtn.setAttribute('aria-label', label);
  }

  #flashAdded(card) {
    const addBtn = card.querySelector('[data-add]');
    if (!addBtn) return;

    this.#cacheAddLabels(addBtn);
    clearTimeout(addBtn._addedTimer);
    addBtn.classList.add('is-added');
    this.#setAddLabel(addBtn, 'Added');

    addBtn._addedTimer = setTimeout(() => {
      this.#setAddLabel(addBtn, 'default');
      addBtn.classList.remove('is-added');
    }, 3000);
  }

  #findVariant(card) {
    return (
      card._variants.find((variant) =>
        variant.options.every((option, index) => String(option) === String(card._selectedOptions[index]))
      ) || card._variants[0]
    );
  }

  #setFilter(btn) {
    this.#activeFilter = btn.dataset.filter || 'all';
    this.querySelectorAll('[data-filter]').forEach((el) => {
      const active = el === btn;
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    this.#visibleCount = this.#pageSize;
    this.#filterCards();
  }

  #filterCards() {
    let matchIndex = 0;
    let matchTotal = 0;

    this.cards.forEach((card) => {
      const tabs = (card.dataset.tabs || '')
        .split(',')
        .map((tab) => tab.trim())
        .filter(Boolean);
      const title = (card.dataset.title || '').toLowerCase();
      const matchesTab = this.#activeFilter === 'all' || tabs.includes(this.#activeFilter);
      const matchesSearch = !this.#searchQuery || title.includes(this.#searchQuery);
      const matches = matchesTab && matchesSearch;

      if (matches) matchTotal += 1;

      const withinPage = matches && matchIndex < this.#visibleCount;
      if (matches) matchIndex += 1;

      card.classList.toggle('is-hidden', !withinPage);
    });

    const hasMore = matchTotal > this.#visibleCount;
    if (this.loadMoreWrap) {
      this.loadMoreWrap.hidden = !hasMore;
    }
  }

  #loadMore() {
    this.#visibleCount += this.#pageSize;
    this.#filterCards();
  }

  #firstEmptyIndex() {
    return this.#selections.findIndex((item) => item == null);
  }

  #addSelection(card) {
    const variant = this.#findVariant(card);
    if (!variant?.available) {
      this.#setStatus('Please choose an available option.');
      return;
    }

    const index = this.#firstEmptyIndex();
    if (index === -1) {
      this.#setStatus(`You've already selected ${this.requiredCount} pieces. Remove one to swap.`);
      return;
    }

    this.#selections[index] = {
      productId: card.dataset.productId,
      productTitle: card.dataset.title,
      variantId: variant.id,
      variantTitle: variant.title === 'Default Title' ? '' : variant.title,
      image: variant.image || card.querySelector('.bundle-card__image')?.currentSrc || '',
      sbHash: variant.sbHash || this.#variantHash(variant.id),
    };
    this.#render();
    this.#flashAdded(card);
    this.#setStatus(`${card.dataset.title} added to your stack.`);
  }

  #onSlotClick(event, index) {
    if (Number.isNaN(index) || this.#selections[index] == null) {
      this.slotsSentinel?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (
      event.target.closest('.bundle-builder__slot-remove') ||
      event.target.closest('.bundle-builder__dock-remove') ||
      event.currentTarget
    ) {
      this.#selections[index] = null;
      this.#render();
      this.#setStatus('Piece removed.');
    }
  }

  #selectedCount() {
    return this.#selections.filter(Boolean).length;
  }

  #paintMainSlot(slot, index) {
    const selection = this.#selections[index];
    const image = slot.querySelector('.bundle-builder__slot-image');
    const title = slot.querySelector('.bundle-builder__slot-title');
    const remove = slot.querySelector('.bundle-builder__slot-remove');
    const empty = selection == null;

    slot.classList.toggle('is-empty', empty);
    slot.setAttribute(
      'aria-label',
      empty ? `Piece ${index + 1} empty` : `Piece ${index + 1}: ${selection.productTitle}`
    );

    if (image) {
      if (selection?.image) {
        image.src = selection.image;
        image.hidden = false;
      } else {
        image.removeAttribute('src');
        image.hidden = true;
      }
    }
    if (title) {
      title.textContent = selection
        ? selection.variantTitle
          ? `${selection.productTitle} / ${selection.variantTitle}`
          : selection.productTitle
        : '';
    }
    if (remove) remove.hidden = empty;
  }

  #paintDockSlot(slot, index) {
    const selection = this.#selections[index];
    const image = slot.querySelector('.bundle-builder__dock-image');
    const remove = slot.querySelector('.bundle-builder__dock-remove');
    const empty = selection == null;

    slot.classList.toggle('is-empty', empty);
    slot.setAttribute(
      'aria-label',
      empty ? `Piece ${index + 1} empty` : `Piece ${index + 1}: ${selection.productTitle}`
    );

    if (image) {
      if (selection?.image) {
        image.src = selection.image;
        image.alt = selection.productTitle || '';
        image.hidden = false;
      } else {
        image.removeAttribute('src');
        image.alt = '';
        image.hidden = true;
      }
    }
    if (remove) remove.hidden = empty;
  }

  #render() {
    const count = this.#selectedCount();
    const remaining = Math.max(this.requiredCount - count, 0);
    const canCheckout = count >= this.requiredCount && Boolean(this.parentVariantId);

    this.selectedCountEls?.forEach((el) => {
      el.textContent = String(count);
    });
    if (this.remainingEl) this.remainingEl.textContent = String(remaining);
    if (this.checkoutBtn) {
      this.checkoutBtn.classList.remove('is-loading');
      this.checkoutBtn.removeAttribute('aria-busy');
      this.checkoutBtn.disabled = !canCheckout;
      this.checkoutBtn.classList.toggle('is-incomplete', count < this.requiredCount);
    }

    document.dispatchEvent(
      new CustomEvent('rvnche:bundle-selection', {
        detail: {
          count,
          required: this.requiredCount,
          complete: count >= this.requiredCount,
        },
      })
    );
    window.__rvncheBundleSelection = {
      count,
      required: this.requiredCount,
      complete: count >= this.requiredCount,
    };

    this.slots.forEach((slot, index) => this.#paintMainSlot(slot, index));
    this.dockSlots.forEach((slot) => this.#paintDockSlot(slot, Number(slot.dataset.dockSlot)));
    this.#persistSelections();
    this.#queueFitStickyPrice();
  }

  #setStatus(message) {
    if (this.statusEl) this.statusEl.textContent = message || '';
  }

  #rootUrl() {
    return window.Shopify?.routes?.root || '/';
  }

  #cartSectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((item) => item.dataset.sectionId)
      .filter(Boolean);
  }

  #openCartDrawer(cart, sections) {
    document.dispatchEvent(
      new CartAddEvent(cart, 'bundle-builder', {
        source: 'bundle-builder',
        sections,
        itemCount: cart?.item_count,
      })
    );
    document.querySelector('cart-drawer-component')?.open?.();
  }

  async #checkout() {
    this.#pruneUnavailableSelections(true);
    this.#render();

    const selections = this.#selections.filter(Boolean);

    if (selections.length !== this.requiredCount) {
      this.#setStatus(`Select ${this.requiredCount} in-stock pieces before checkout.`);
      return;
    }

    if (selections.some((selection) => !this.#isSelectionAvailable(selection))) {
      this.#pruneUnavailableSelections(true);
      this.#render();
      this.#setStatus(`Select ${this.requiredCount} in-stock pieces before checkout.`);
      return;
    }

    if (!this.parentVariantId) {
      this.#setStatus('Set the BYOB / bundle product in the theme editor first.');
      return;
    }

    const checkoutToken = ++this.#checkoutToken;
    this.checkoutBtn.disabled = true;
    this.checkoutBtn.classList.add('is-loading');
    this.checkoutBtn.setAttribute('aria-busy', 'true');
    this.#setStatus('Adding to cart…');

    const bundleSelection = selections
      .map((selection) => selection.sbHash || this.#variantHash(selection.variantId))
      .join(' <> ');

    const properties = {
      _bundle_selection: bundleSelection,
    };

    try {
      await fetch(`${this.#rootUrl()}cart/clear.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });

      const response = await fetch(`${this.#rootUrl()}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: this.parentVariantId,
              quantity: 1,
              properties,
            },
          ],
          sections: this.#cartSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Unable to add bundle');
      }

      if (checkoutToken !== this.#checkoutToken) return;
      this.#openCartDrawer(data, data.sections);
      this.#setStatus(this.dataset.addSuccess || 'Added to cart');
      this.#render();
    } catch (error) {
      if (checkoutToken !== this.#checkoutToken) return;
      this.#setStatus(error?.message || 'Something went wrong starting checkout.');
      this.#render();
    }
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
